/**
 * Sprint 14.1b — Banner shown on closed evaluations.
 *
 * Renders directly under the Evaluations pill row when the selected
 * evaluation has status='closed'. Stage action buttons consume the
 * isClosed signal independently — this banner is the visual cue.
 */
import React from 'react';
import { Lock } from 'lucide-react';
import { fmtDateIST } from '@/lib/format';

const fmt = (iso) => {
  if (!iso) return null;
  try {
    return fmtDateIST(iso, {
      day: '2-digit', month: 'short', year: 'numeric',
    }, null);
  } catch {
    return null;
  }
};

export function ClosedEvalBanner({ evaluation }) {
  if (!evaluation || evaluation.status !== 'closed') return null;
  const closedOn = fmt(evaluation.closed_at);
  return (
    <div
      role="alert"
      className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
      data-testid="closed-evaluation-banner"
    >
      <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <div className="flex-1">
        <p className="font-semibold">Read-only — this evaluation is closed.</p>
        <p className="text-xs">
          Stage actions are disabled. The AI Report remains viewable and downloadable.
          {closedOn && <span className="ml-1">Closed on {closedOn}.</span>}
        </p>
        {evaluation.closed_reason && (
          <p className="mt-1 text-xs italic text-amber-800">
            Reason: {evaluation.closed_reason}
          </p>
        )}
      </div>
    </div>
  );
}
